import { gql } from '@apollo/client'
import { Button, Chip, Heading, Information, Modal } from '@farol-ds/react'
import { useMetrics } from '@jusbrasil-web/docview/core'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  COMPONENT_CLASS,
  FEATURE_CLASS,
  FEATURE_NAME,
  GA4CustomComponentRenderedEvent,
  GA4FeatureUseEvent,
  REQUIRED_USER_STATUS,
} from '@jusbrasil-web/shared-ga4-events'
import { useEffect } from 'react'

import { LawsuitDecision_DocumentFragment } from './__generated__/lawsuit-decision.graphql'
import styles from './lawsuit-decision.module.scss'

export interface LawsuitDecisionProps {
  document: LawsuitDecision_DocumentFragment
}

const LAWSUIT_DECISION_OPTIONS = {
  PROCEDENTE: {
    label: 'Procedente',
    chipKind: 'positive',
  },
  PARCIALMENTE_PROCEDENTE: {
    label: 'Parcialmente procedente',
    chipKind: 'positive',
  },
  IMPROCEDENTE: {
    label: 'Improcedente',
    chipKind: 'negative',
  },
} as const

export function LawsuitDecision(props: LawsuitDecisionProps) {
  const { document } = props
  const { lawsuitDecision, viewerCanAccessFullDocument } = document

  const { ref: dispatcherRef, sendMetrics } = useMetrics<HTMLDivElement>()
  const { sendGA4Event } = useGA4Dispatcher()

  const result =
    LAWSUIT_DECISION_OPTIONS[lawsuitDecision?.result as keyof typeof LAWSUIT_DECISION_OPTIONS]

  useEffect(() => {
    if (result) {
      sendMetrics('Peca.LawsuitDecisionComponentViewed', {
        metadata: { lawsuit_decision: lawsuitDecision?.result },
      })
      sendGA4Event(
        new GA4CustomComponentRenderedEvent({
          componentClass: COMPONENT_CLASS.LAWSUIT_STATUS,
          resourceId: document.boxResourceId,
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!result) {
    return null
  }
  const text = lawsuitDecision?.text

  const handleClick = () => {
    sendMetrics('Peca.LawsuitProgressModalOpened', {
      metadata: { lawsuit_decision: lawsuitDecision?.result },
    })
    sendGA4Event(
      new GA4FeatureUseEvent({
        featureClass: FEATURE_CLASS.VERIFY_DOCUMENTS,
        featureName: FEATURE_NAME.VERIFY_LAWSUIT_UPDATE,
        resourceId: document.boxResourceId,
        requiredUserStatus: REQUIRED_USER_STATUS.SUBSCRIBER_USER,
        componentClass: COMPONENT_CLASS.LAWSUIT_STATUS,
        successfulAttempt: true,
      })
    )
  }

  return (
    <div className={styles.wrapper} ref={dispatcherRef}>
      <div className={styles.headingWithInfo}>
        <Heading size="md" asChild>
          <h2 className="widgetStyles.cardTitle">Decisão do 1º grau</h2>
        </Heading>
        <Information
          label="O resultado da decisão foi extraído do andamento do processo e interpretado pelo Jusbrasil para facilitar a compreensão do desfecho do caso."
          size="xs"
        />
      </div>

      <Chip kind={result.chipKind} size="sm">
        {result.label}
      </Chip>

      {text && viewerCanAccessFullDocument && (
        <Modal size="sm">
          <Modal.Trigger>
            <Button kind="plain" className={styles.modalTrigger} onClick={handleClick}>
              Mostrar andamento
            </Button>
          </Modal.Trigger>
          <Modal.Content>
            <Modal.IconClose />
            <Modal.Header>
              <Modal.HeaderTitle>Decisão extraída do andamento do processo</Modal.HeaderTitle>
            </Modal.Header>
            <Modal.Body>{text}</Modal.Body>
          </Modal.Content>
        </Modal>
      )}
    </div>
  )
}

LawsuitDecision.fragments = {
  document: gql`
    fragment LawsuitDecision_document on Document {
      lawsuitDecision {
        text
        result
      }
      viewerCanAccessFullDocument
      boxResourceId
    }
  `,
}
